// Web Component: MainProduct
// Gestisce la logica della pagina prodotto, inclusi eventi GTM, caricamento dati e interazione con analytics.
// Attributi richiesti: presenza di #product-json
// Dipendenze: Utils, Shopify.analytics, presenza di form e data-gtm-action
// Limiti: richiede struttura HTML compatibile e dati JSON corretti
// Utilizzato nelle pagine prodotto
// Cache #product-json: MainProduct.getProductJson() e' l'unico punto che parsa #product-json
// ed e' condiviso con VariantChanger e StoqBtn (vedi commento sul metodo).
class MainProduct extends HTMLElement {
  form; loader; productData; 
  static s = {
    checkout: '[data-checkout]'
  }
  static productJsonCache = null; // { node, data } dell'ultimo #product-json parsato

  // Restituisce l'oggetto completo { product, variant, currencyCode } di #product-json.
  // Il parse avviene una sola volta per nodo: il reload-url di helper-product_json, a ogni
  // cambio variante (e a ogni evento carrello), sostituisce l'innerHTML e crea un nodo nuovo,
  // quindi l'identita' del nodo basta come invalidazione — niente confronto di stringhe,
  // niente timer. Il prodotto completo (varianti, immagini, descrizione) pesa: parsarlo a
  // ogni lettura era il costo principale sul main thread durante gli swap (INP).
  // Nodo assente → null senza lanciare; JSON non valido → null (con errore in console),
  // memorizzato per quel nodo cosi' non viene ritentato a ogni lettura.
  static getProductJson() {
    const node = document.querySelector('#product-json');
    if(!node) {
      return null;
    }
    const cache = MainProduct.productJsonCache;
    if(cache && cache.node === node) {
      return cache.data;
    }
    let data = null;
    try {
      data = JSON.parse(node.textContent);
    } catch (error) {
      console.error('MainProduct: error parsing #product-json', error);
    }
    MainProduct.productJsonCache = { node, data };
    return data;
  }

  constructor() {
    super();
    this.loader = document.querySelector('theme-loader[data-global]');
    this.init();
  }

  connectedCallback() {
    const json = MainProduct.getProductJson();
    if(!json) {
      console.error('MainProduct: #product-json not found or invalid.');
      return;
    }
    this.productData = json.product;
    this.variantData = json.variant;
    this.currencyCode = json.currencyCode;
    this.querySelectorAll('[data-gtm-action]').forEach(el => {
      el.addEventListener('click', () => {
        const action = el.dataset.gtmAction;
        if(el.classList.contains('collapsed')){
          return;
        }
        //repeated to get the correct variant: rilegge al click la variante CORRENTE.
        //Non va cachata sull'istanza: il reload-url sostituisce #product-json a ogni cambio
        //variante, e la cache per nodo ne restituisce la versione aggiornata senza riparsare.
        this.variantData = MainProduct.getProductJson()?.variant;
        Shopify.analytics.publish(`pdp:interaction`, { productTitle: this.productData.title, variantSKU: this.variantData.sku, action: action });
      });
    });
    Shopify.analytics.publish('product:viewed', { ...this.variantData, product: this.productData, currencyCode: this.currencyCode });
  }

  init = () => {
    this.form = this.querySelector('form');
    // Chiamato dal constructor: se #product-json non e' ancora nel DOM, productData resta
    // undefined e connectedCallback lo riassegna.
    this.productData = MainProduct.getProductJson() ?? undefined;
    this.querySelectorAll(MainProduct.s.checkout).forEach(button => {
      button.addEventListener('click', () => this.purchase());
    });
  }

  apps = () => {
    this.feedaty();
  }

  feedaty = () => {
    const script = document.querySelector('#qa-script');
    if(script){
      const newScript = script.cloneNode();
      script.remove();
      document.body.appendChild(newScript);
    }
    FeedatyWidget.init();
    feedatyQaInit();
  }

  addToCart = () => {
    this.showLoader();
    Utils.addTocart({
      form: this.form,
      finally: () => this.hideLoader()
    });
  }

  purchase = () => {
    this.showLoader();
    Utils.addTocart({
      data: new FormData(this.form),
      success: () => {
        window.location.href = Shopify.routes.root + 'checkout';
      }
    });
  }

  showLoader = () => {
    if(this.loader)
      this.loader.show();
  }

  hideLoader = () => {
    if(this.loader)
      this.loader.hide();
  }
}
if(!customElements.get('main-product')) {
  customElements.define('main-product', MainProduct);
}

class ProductSticky extends HTMLElement {
  static hiddenClasses = ['invisible'];

  connectedCallback() {
    setTimeout(() => this.observe());
  }

  observe = () => {
    if(!this.dataset.observe) {
      console.error('ProductSticky: data-observe attribute is required.');
      return;
    }
    const buttons = document.querySelector(this.dataset.observe);
    if(!buttons) {
      console.error('ProductSticky: observed element not found.', this.dataset.observe);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.hide();
        } else if(entry.boundingClientRect.top < 0) {
          this.show();
        }
      });
    });
    observer.observe(buttons);
  }

  show = () => {
    ProductSticky.hiddenClasses.forEach(className => {
      this.classList.remove(className);
    });
  }

  hide = () => {
    ProductSticky.hiddenClasses.forEach(className => {
      this.classList.add(className);
    });
  }
}
if(!customElements.get('product-sticky')) {
  customElements.define('product-sticky', ProductSticky);
}

// Web Component: StickySizeSelect
// Gestisce apertura/chiusura del selettore taglia della barra sticky.
// - Mobile: bottom sheet con backdrop; Desktop: dropdown ancorato (stile via base.css).
// - Delega i click cosi' i listener sopravvivono al reload-url che rigenera trigger,
//   lista e bottone add al cambio variante.
// Trigger di apertura: .js-size-trigger (toggle) e [data-open-size] (bottone add senza
// taglia scelta). Chiusura: .js-size-close (X e backdrop), Escape, e la selezione di una
// taglia (.js-size-row → variant-changer, che poi ricarica il contenuto aggiornato).
// Quick-add: se il pannello viene aperto dal bottone "Aggiungi al carrello" (intento
// pendingAdd), selezionare una taglia aggiunge direttamente il prodotto al carrello —
// dopo che il reload-url ha rigenerato il <cart-add>, viene attivato via MutationObserver.
// Se il pannello viene aperto manualmente dal selettore, si limita a scegliere e chiudere.
// Vale sia per click sia per selezione da tastiera (Enter/Space) per accessibilita'.
// Blocco dello scroll (mobile, bottom sheet aperto): solo CSS in base.css — il backdrop copre
// il viewport con touch-action: none e la lista ha overscroll-behavior: contain. Il <body> NON
// si tocca: prima open()/close() aggiungevano/toglievano una classe sul body, e siccome il body
// porta classi con regole discendenti, ogni cambio ricalcolava lo stile dell'intero documento
// (~2750 elementi, ~50 ms nel frame dell'interazione con CPU 4x: misurato 139,6 ms il toggle
// della classe sul body contro 4,6 ms della classe open su questo elemento). Era il costo
// principale dell'INP dello sticky, a ogni apertura e chiusura.
class StickySizeSelect extends HTMLElement {
  static OPEN = 'open';
  static AUTOADD_TIMEOUT = 5000; // ms: guardia se il <cart-add> non compare dopo il reload

  // Intento con cui e' stato aperto il pannello: true = aperto dal bottone add
  // (→ auto-add alla scelta taglia), false = aperto manualmente dal selettore.
  pendingAdd = false;

  connectedCallback() {
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onRowKeydown.bind(this));
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  onClick = (e) => {
    if(e.target.closest('.js-size-close')) {
      e.preventDefault();
      this.close();
      return;
    }
    if(e.target.closest('.js-size-trigger')) {
      e.preventDefault();
      this.pendingAdd = false; // apertura manuale: nessun auto-add
      this.toggle();
      return;
    }
    if(e.target.closest('[data-open-size]')) {
      e.preventDefault();
      this.pendingAdd = true; // aperto dal bottone add: auto-add alla scelta taglia
      this.open();
      return;
    }
    // Selezione di una taglia: chiudi (variant-changer gestisce il cambio variante).
    if(e.target.closest('.js-size-row')) {
      this.onRowSelected();
    }
  }

  // Selezione taglia da tastiera (Enter/Space): stesso comportamento del click.
  // variant-changer intercetta gia' il keydown per cambiare variante; qui gestiamo
  // solo chiusura + eventuale auto-add.
  onRowKeydown = (e) => {
    if((e.key === 'Enter' || e.key === ' ') && e.target.closest('.js-size-row')) {
      this.onRowSelected();
    }
  }

  // Taglia scelta: leggi l'intento prima di chiudere (close azzera pendingAdd) e,
  // se il pannello era stato aperto dal bottone add, programma l'aggiunta al carrello.
  onRowSelected = () => {
    const wasPendingAdd = this.pendingAdd;
    this.close();
    if(wasPendingAdd) this.scheduleAutoAdd();
  }

  // Attende che il reload-url del bottone add rigeneri il <cart-add> (dopo il cambio
  // variante innescato da variant-changer) e lo attiva, come un click reale.
  scheduleAutoAdd = () => {
    const host = this.querySelector('reload-url[data-section="helper-product-add"]');
    if(!host) return;
    let timeoutId;
    const observer = new MutationObserver((mutations, obs) => {
      const cartAdd = host.querySelector('cart-add');
      if(cartAdd) {
        obs.disconnect();
        clearTimeout(timeoutId);
        cartAdd.click();
      }
    });
    observer.observe(host, { childList: true, subtree: true });
    timeoutId = setTimeout(() => observer.disconnect(), StickySizeSelect.AUTOADD_TIMEOUT);
  }

  onKeydown(e) {
    if(e.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }

  isOpen = () => this.classList.contains(StickySizeSelect.OPEN);

  toggle = () => {
    this.isOpen() ? this.close() : this.open();
  }

  open = () => {
    this.classList.add(StickySizeSelect.OPEN);
    this.setExpanded(true);
  }

  close = () => {
    this.classList.remove(StickySizeSelect.OPEN);
    this.setExpanded(false);
    this.pendingAdd = false;
  }

  setExpanded = (value) => {
    const trigger = this.querySelector('.js-size-trigger');
    if(trigger) {
      trigger.setAttribute('aria-expanded', value ? 'true' : 'false');
    }
  }
}
if(!customElements.get('sticky-size-select')) {
  customElements.define('sticky-size-select', StickySizeSelect);
}

/*
//gestisce il trova in negozio in pp con onestock
class StoreStock extends HTMLElement {

  i18n;     // traduzioni
  province; // la provincia del cliente (sigla)
  zip;      // il cap del cliente 
  stores;   // elenco sedi
  stocks;   // risposta di onestock per la variante
  merged;   // elenco dei negozi con relativa disponibilità
  store;    // id dello store preferito
  unified;  // sede unified dall'elenco merged

  static S = {
    saveProvince: '[data-province-save]',
    provinceInput: '#store-province-input',
    deliveryTitle: '[data-delivery-title]',
    deliveryDescription: '[data-delivery]',
    storeList: '[data-store-list]',
    storeTitle: '[data-store-title]',
    storeInner: '[data-store]',
    storeItem: '[data-store-item]'
  }
  static UNIFIED = 'XY'; // codice dello store unified
  static URLSTORES = `${Utils.API_URL}/api/fidelity/shops`;
  static URLSTOCK = `${Utils.API_URL}/api/shop/${Shopify.shop}/product-stocks-by-sku-codes`;
  static MAP_API = 'AIzaSyDzJfY1o0dk-bSGQPKFiur1uBzt2TjVYRE';

  connectedCallback() {
    this.i18n = JSON.parse(this.dataset.i18n);

    this.addEventListener(Utils.E_STORES, () => {
      this.merge();
    });
    this.addEventListener(Utils.E_STORE_STOCK, () => {
      this.merge();
    });
    this.addEventListener(Utils.E_STORE_MERGED, () => {
      this.getProvince();
      this.showStores();
    });
    this.addEventListener(Utils.E_STORE_PROVINCE, () => {
      this.getStore();
      this.showProvince();
      this.fillZipInput();
    });
    this.addEventListener(Utils.E_STORE_FOUND, () => {
      this.showStore();
    });
    
    this.getStores();
    this.getStocks();
    this.handleProvince();
  }

  // trova la provincia geolocalizzando o controllando se è salvata nel localStorage o se la ha nei dataset
  getProvince = () => {
    this.province = this.dataset.province || localStorage.getItem('province');
    this.zip = this.dataset.zip || localStorage.getItem('zip');
    if(!this.province){
      if(navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          const latlng = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
          this.geocode({ 'latLng': latlng });
        });
      } else {
        console.log('stock error');
      }
      this.getStore();
    } else {
      localStorage.setItem('province', this.province);
      this.dispatchEvent(new CustomEvent(Utils.E_STORE_PROVINCE, { detail: this.province }));
    }
  }

  // trova lo store preferito o lo cerca tra quelli della provincia
  getStore = () => {
    this.store = localStorage.getItem('favourite_shop');
    if(this.store) {
      this.dispatchEvent(new CustomEvent(Utils.E_STORE_FOUND, { detail: this.store }));
    } else if(this.province) {
      this.store = this.stores.find(store => store.address?.provinceCode == this.province)?.shop_id.value;
      if(this.store) {
        localStorage.setItem('favourite_shop', this.store);
      }
      this.dispatchEvent(new CustomEvent(Utils.E_STORE_FOUND, { detail: this.store }));
    }
  }

  // ottiene tutti i negozi o dal sessionStorage o dal server
  getStores = () => {
    this.stores = JSON.parse(sessionStorage.getItem('stores'));
    if(!this.stores) {
      axios({
        url: StoreStock.URLSTORES,
        method: 'get',
        withCredentials: true
      })
      .then((response) => {
        this.stores = response.data;
        sessionStorage.setItem('stores', JSON.stringify(this.stores));
        this.dispatchEvent(new CustomEvent(Utils.E_STORES, { detail: this.stores }));
      }).catch((error) => {
        Utils.errorHandler({
          message: error.message,
          target: 'store-stock',
          error: error,
          type: 'axios'
        });
      });
    } else {
      this.dispatchEvent(new CustomEvent(Utils.E_STORES, { detail: this.stores }));
    }
  }

  // ottiene le disponibilità della variante da onestock e pubblica un evento al success
  getStocks = () => {
    axios({
      url: StoreStock.URLSTOCK,
      method: 'post',
      data: JSON.stringify(this.dataset.sku.split(',')),
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((response) => {
      this.stocks = response.data;
      this.dispatchEvent(new CustomEvent(Utils.E_STORE_STOCK, { detail: this.stocks }));
    }).catch((error) => {
      Utils.errorHandler({
        message: error.message,
        target: 'store-stock',
        error: error,
        type: 'axios'
      });
    });
  }

  // popola l'elenco merged con le disponibilità
  merge = () => {
    if(this.stores && this.stocks) {
      this.merged = this.stores.map(store => {
        const stock = this.stocks.find(stock => stock.storeCode == store.shop_id.value);
        return {
          ...store,
          stock: stock
        }
      });
      this.unified = this.merged.find(store => store.shop_id.value == StoreStock.UNIFIED);
      this.dispatchEvent(new CustomEvent(Utils.E_STORE_MERGED, { detail: this.merged }));
    }
  }

  // mostra la provincia selezionata e la disponibilità di consegna
  showProvince = () => {
    if(this.province && this.unified){
      const deliveryTitle = this.querySelector(StoreStock.S.deliveryTitle);
      if(deliveryTitle){
        deliveryTitle.innerText = this.i18n.delivery_to.replace('{{ province }}', this.zip);
      }
      const deliveryDescription = this.querySelector(StoreStock.S.deliveryDescription);
      if(deliveryDescription){
        const type = this.unified.stock?.quantity > 0 ? (this.unified.stock.quantity < 3 ? 'low-stock' : 'available') : 'unavailable';

        if(type == 'available') {
          this.getDelivery(deliveryDescription);
          return;
        }

        if(type == 'low-stock') {
          this.getDelivery2(deliveryDescription);
          return;
        }

        const template = this.getTemplate(type);
        deliveryDescription.innerHTML = template.innerHTML;
      }
    }
  }

  getDelivery2 = (deliveryDescription) => {
    const json = JSON.parse(this.querySelector('[data-shipping').innerText);
    const template = this.getTemplate('low-stock');
    const filtered = json.filter(r => r.country.toUpperCase() == this.dataset.country && r.min <= r.product_price && r.max >= r.product_price);

    // ORDINA prima Standard, poi Express, poi il resto
    filtered.sort((a, b) => {
      const priority = name => {
        if (name === "Standard") return 0;
        if (name === "Express") return 1;
        return 2;
      };
      return priority(a.name) - priority(b.name);
    });
    
    const deliveries = filtered.map(delivery => {
      return this.calcDelivery(delivery);
    });
    deliveryDescription.innerHTML = template.innerHTML.replaceAll('{delivery}', deliveries.join(''));
  }

  getDelivery = (deliveryDescription) => {
    const json = JSON.parse(this.querySelector('[data-shipping').innerText);
    const template = this.getTemplate('available');
    const filtered = json.filter(r => r.country.toUpperCase() == this.dataset.country && r.min <= r.product_price && r.max >= r.product_price);

    // ORDINA prima Standard, poi Express, poi il resto
    filtered.sort((a, b) => {
      const priority = name => {
        if (name === "Standard") return 0;
        if (name === "Express") return 1;
        return 2;
      };
      return priority(a.name) - priority(b.name);
    });
    
    const deliveries = filtered.map(delivery => {
      return this.calcDelivery(delivery);
    });
    deliveryDescription.innerHTML = template.innerHTML.replaceAll('{delivery}', deliveries.join(''));
  }

  calcDelivery = (delivery) => {
    const template = this.getTemplate('delivery');
    const price = Number(delivery.price);
    const priceString = price > 0 ? Utils.money(price) : this.i18n.stock.free_html;
    const from = this.calcDeliveryDate(delivery.from);
    const to = this.calcDeliveryDate(delivery.to);

    // Mostra solo se il prezzo è "gratis"
    const showPrice = priceString === this.i18n.stock.free_html ? ` (${priceString})` : '';
    
    return template.innerHTML
      .replaceAll('{name}', delivery.name)
      .replaceAll('{from}', from)
      .replaceAll('{to}', to)
      .replaceAll('{price}', showPrice);
  }

  calcDeliveryDate = (days) => {
    let date = new Date();
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) { // 0 = Sunday, 6 = Saturday
        added++;
      }
    }
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  }

  // mostra la disponibilità nello store selezionato
  showStore = () => {
    if(this.merged && this.store){
      console.log('store', this.store);
      const store = this.merged.find(store => store.shop_id.value == this.store);

      const storeTitle = this.querySelector(StoreStock.S.storeTitle);
      const storeInner = this.querySelector(StoreStock.S.storeInner);

      storeTitle.innerText = store.name;
      const template = this.getTemplate('store-stock').innerHTML;
      var onlineTemplate, storeTemplate;

      if(store.stock?.quantity > 0) {
        if(store.stock.quantity >= 2){
          onlineTemplate = this.getTemplate('online-available').innerHTML.replaceAll('{text}', this.i18n.online_store.store_available_html);
          storeTemplate = this.getTemplate('store-available').innerHTML;
        } else{
          onlineTemplate = this.getTemplate('online-available').innerHTML.replaceAll('{text}', this.i18n.online_store.store_low_html);
          storeTemplate = this.getTemplate('store-low').innerHTML;
        }
      } else {
        storeTemplate = this.getTemplate('store-unavailable').innerHTML;
        if(this.unified?.stock.quantity >= 3) {
          onlineTemplate = this.getTemplate('online-available').innerHTML.replaceAll('{text}', this.i18n.online_store.unified_available_html);
        } else if(this.unified?.stock.quantity > 0) {
          onlineTemplate = this.getTemplate('online-low').innerHTML;
        } else {
          onlineTemplate = this.getTemplate('online-unavailable').innerHTML;
        }
      }
      storeInner.innerHTML = template.replaceAll('{online}', onlineTemplate).replaceAll('{store}', storeTemplate);
    }
    

  }

  // mostra i negozi nell'offcanvas e gestisce il click
  showStores = () => {
    if(this.merged) {
      const list = this.querySelector(StoreStock.S.storeList);
      if(list){
        this.merged.filter(store => store.shop_id.value != StoreStock.UNIFIED).forEach(store => {
          const template = this.getTemplate('store-item');
          list.innerHTML += template.innerHTML
            .replaceAll('{id}', store.shop_id.value)
            .replaceAll('{name}', store.name)
            .replaceAll('{address}', `${store.address.address1}<br>${store.address.zip} ${store.address.city} (${store.address.provinceCode})`);
        });
        this.querySelectorAll(StoreStock.S.storeItem).forEach(item => {
          item.addEventListener('click', () => {
            this.store = item.dataset.storeItem;
            localStorage.setItem('favourite_shop', this.store);
            this.dispatchEvent(new CustomEvent(Utils.E_STORE_FOUND, { detail: this.store }));
          });
        });
      }
    }
  }

  // gestisce la input di selezione della provincia
  handleProvince = () => {
    const button = this.querySelector(StoreStock.S.saveProvince);
    button.addEventListener('click', () => {
      const input = this.querySelector(StoreStock.S.provinceInput);
      if(input.value.trim() != this.zip){
        this.geocode({ address: input.value });
      } else {
        this.showError();
      }
      this.dispatchEvent(new CustomEvent(Utils.E_STORE_PROVINCE, { detail: this.province }));
    });
  }

  // geolocalizza in base a data e restituisce la provincia e il cap
  geocode = (data) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(data, (results, status) => {
      if(status === google.maps.GeocoderStatus.OK){
        this.province = results[0].address_components.find(component => {
          return component.types.includes('administrative_area_level_2');
        });
        this.zip = results[0].address_components.find(component => {
          return component.types.includes('postal_code');
        });
        this.province = this.province?.short_name;
        this.zip = this.zip?.short_name;
        if(this.province && this.zip) {
          localStorage.setItem('province', this.province);
          localStorage.setItem('zip', this.zip);
          this.dispatchEvent(new CustomEvent(Utils.E_STORE_PROVINCE, { detail: this.province }));
        } else {
          this.showProvinceError();
        }
      } else {
        this.showProvinceError();
      }
    });
  }

  showProvinceError = () => {
    Utils.errorHandler({
      message: this.i18n.province.error,
      target: 'store-stock',
      type: 'province',
      popup: true
    })
  }

  // ottiene il template che si desidera
  getTemplate = (name) => {
    return this.querySelector(`template[name="${name}"]`);
  }

  // mostra il cap selezionato di default
  fillZipInput = () => {
    const input = this.querySelector(StoreStock.S.provinceInput);
    if(input && this.zip){
      input.value = this.zip;
    }
  }


}
if(!customElements.get('store-stock')) {
  customElements.define('store-stock', StoreStock);
}
*/
class StoqBtn extends HTMLElement {
  productData;

  connectedCallback() {
    this.productData = MainProduct.getProductJson(); // cache condivisa, vedi MainProduct
    if(!this.productData || !this.productData.variant || !this.productData.variant.id || !this.productData.product) {
      console.error("StoqBtn: No product data found.");
      return;
    }

    this.addEventListener('click', this.openStoq.bind(this));
  }

  openStoq = () => {
    if(!window._RestockRocket) {
      console.error("StoqBtn: RestockRocket is not loaded.");
      return;
    }
    window._RestockRocket.openModal(this.productData.product, this.productData.variant.id);
    window.dispatchEvent(new CustomEvent(Utils.E_BACKSTOCK));
    Shopify.analytics.publish(Utils.E_BACKSTOCK);
  }
}
if(!customElements.get('stoq-btn')) {
  customElements.define('stoq-btn', StoqBtn);
}

// Web Component: ReloadUrl
// Ricarica il proprio contenuto tramite Section Rendering quando cambia la query string
// (es. ?variant= al cambio taglia) e, con data-cart, sugli eventi carrello.
// Attributi:
// - data-section (obbligatorio): senza data-group e' l'id della sezione da richiedere
//   (?section_id=...); con data-group e' il nome del frammento da prelevare nella risposta
//   di gruppo (per convenzione lo stesso nome della sezione helper singola che sostituisce,
//   cosi' i selettori esistenti — es. StickySizeSelect.scheduleAutoAdd — restano validi).
// - data-cart (opzionale): ricarica anche su Utils.E_CART_ADDED / UPDATED / REMOVED.
// - data-group (opzionale): id della sezione "di gruppo" che rende piu' frammenti insieme,
//   ognuno in un <template data-fragment="<nome>">.
// Modo gruppo (INP): sulla PDP i reload-url sono molti e prima ognuno faceva la propria
// richiesta e il proprio swap, in task separati — N render server, N cicli di parse/style/
// layout a ridosso del click. Gli host con lo stesso data-group non fanno fetch da soli: si
// registrano nel coordinatore del gruppo (ReloadUrlGroup), che fa UNA richiesta e sostituisce
// tutti i frammenti nello stesso task. Senza data-group il comportamento e' quello storico.
// Un solo patch di history: il monitoraggio della query e' statico (ReloadUrl.watchQuery),
// installato una volta sola; prima ogni istanza avvolgeva pushState/replaceState e un singolo
// replaceState attraversava una catena di N wrapper.
// Dipendenze: axios, Utils (errorHandler, costanti eventi carrello).
class ReloadUrl extends HTMLElement {
  static groups = new Map();          // data-group → ReloadUrlGroup
  static queryCallbacks = new Set();  // callback iscritte a watchQuery
  static prevSearch = null;           // ultima query string notificata (una sola, globale)
  static historyPatched = false;

  group = null;         // coordinatore di gruppo, se data-group
  unwatchQuery = null;  // cleanup dell'iscrizione a watchQuery (modo singolo)

  // Iscrive una callback ai cambi di query string. Al primo uso avvolge UNA volta
  // history.pushState/replaceState e registra popstate/hashchange; le iscrizioni successive
  // si aggiungono al Set senza toccare history. La callback riceve
  // { oldSearch, newSearch, url }. Restituisce la funzione che annulla l'iscrizione.
  static watchQuery(cb) {
    if(!ReloadUrl.historyPatched) {
      ReloadUrl.historyPatched = true;
      ReloadUrl.prevSearch = location.search;
      const notify = ReloadUrl.notifyQuery;

      // back/forward
      window.addEventListener('popstate', notify);
      // opzionale: se cambi anche il fragment
      window.addEventListener('hashchange', notify);

      // intercetta pushState/replaceState
      const _push = history.pushState;
      history.pushState = function (...args) {
        const ret = _push.apply(this, args);
        notify();
        return ret;
      };
      const _replace = history.replaceState;
      history.replaceState = function (...args) {
        const ret = _replace.apply(this, args);
        notify();
        return ret;
      };
    }
    ReloadUrl.queryCallbacks.add(cb);
    return () => ReloadUrl.queryCallbacks.delete(cb);
  }

  // Confronto unico e globale con l'ultima query notificata. Si itera su una copia del Set:
  // una callback puo' (indirettamente) iscrivere o disiscrivere host. Un errore in una
  // callback non impedisce alle altre di partire.
  static notifyQuery = () => {
    if(location.search === ReloadUrl.prevSearch) return;
    const query = { oldSearch: ReloadUrl.prevSearch, newSearch: location.search, url: location.href };
    ReloadUrl.prevSearch = location.search;
    [...ReloadUrl.queryCallbacks].forEach((cb) => {
      try {
        cb(query);
      } catch (error) {
        console.error('ReloadUrl: query change callback failed', error);
      }
    });
  }

  connectedCallback() {
    if(this.dataset.group) {
      if(!this.dataset.section) {
        console.error('ReloadUrl: no fragment specified in data-section attribute.', this);
        return;
      }
      let group = ReloadUrl.groups.get(this.dataset.group);
      if(!group) {
        group = new ReloadUrlGroup(this.dataset.group);
        ReloadUrl.groups.set(this.dataset.group, group);
      }
      this.group = group;
      group.add(this);
      return;
    }

    this.unwatchQuery = this.onQueryChange(this.reload.bind(this));
    if(this.dataset.cart != undefined){
      window.addEventListener(Utils.E_CART_UPDATED, this.onCart);
      window.addEventListener(Utils.E_CART_REMOVED, this.onCart);
      window.addEventListener(Utils.E_CART_ADDED, this.onCart);
    }
  }

  // Host rimosso (es. blocco tolto o sezione ri-renderizzata dall'editor): esce dal gruppo
  // o annulla le proprie iscrizioni, cosi' non restano host morti che fanno richieste.
  disconnectedCallback() {
    if(this.group) {
      this.group.remove(this);
      this.group = null;
    }
    if(this.unwatchQuery) {
      this.unwatchQuery();
      this.unwatchQuery = null;
    }
    window.removeEventListener(Utils.E_CART_UPDATED, this.onCart);
    window.removeEventListener(Utils.E_CART_REMOVED, this.onCart);
    window.removeEventListener(Utils.E_CART_ADDED, this.onCart);
  }

  onCart = () => this.reload({ url: location.href });

  reload = (query) => {
    if(this.group) {
      this.group.reload(query); // modo gruppo: la richiesta la fa il coordinatore
      return;
    }
    // Log disattivato (INP): console.log costa ~0,7 ms per click sui device lenti.
    // console.log('ReloadUrl: query changed', query);
    if(!this.dataset.section){
      console.error('ReloadUrl: no section specified in data-section attribute.');
      return;
    }

    this.classList.add('loading');
    const url = new URL(query.url);
    url.searchParams.set('section_id', this.dataset.section);

    axios.get(url.toString()).then((response) => {
      this.reloaded(response, url);
    }).catch((error) => {
      Utils.errorHandler({
        message: error.response?.data?.message || error.message,
        target: 'reload-url',
        error: error,
        type: 'axios'
      });
    }).finally(() => {
      this.classList.remove('loading');
    });
  }

  // Solo modo singolo: il modo gruppo applica i frammenti in ReloadUrlGroup.apply.
  reloaded = (response, url) => {
    this.innerHTML = response.data;
  }

  // Compatibilita': wrapper d'istanza su ReloadUrl.watchQuery. Restituisce il cleanup, che
  // ora rimuove solo questa callback (prima ripristinava history, spezzando la catena dei
  // wrapper installati dalle altre istanze).
  onQueryChange(cb) {
    return ReloadUrl.watchQuery(cb);
  }

}

// Coordinatore di un gruppo di reload-url (vedi commento su ReloadUrl, "Modo gruppo").
// - Una richiesta ?section_id=<gruppo> all'URL corrente per ogni cambio di query e, se
//   almeno un host membro ha data-cart, per ogni evento carrello.
// - Risposta parsata in un <template> staccato: niente upgrade di custom element ne'
//   esecuzione di script nel parsing intermedio. Poi, in UN SOLO task sincrono, ogni host
//   ancora connesso riceve il contenuto del proprio <template data-fragment>. Host senza
//   frammento nella risposta: lasciato com'e'.
// - Ordine: prima l'host di FIRST_FRAGMENT (#product-json), cosi' i componenti ricreati dagli
//   swap successivi leggono gia' il nodo nuovo tramite la cache MainProduct.getProductJson();
//   poi gli altri nell'ordine di registrazione.
// - Risposte fuori ordine: ogni richiesta prende un numero progressivo e si applica solo la
//   risposta dell'ultima lanciata. Con due tap rapidi su due taglie, se la prima risposta
//   arriva dopo la seconda viene scartata: la pagina resta sull'ultima taglia scelta (che e'
//   anche quella nell'URL). Errori e fine caricamento di una richiesta superata sono ignorati:
//   la classe loading la toglie la richiesta piu' recente.
// - Classe loading differita al frame successivo (requestAnimationFrame), vedi reload.
// - Errore: un solo Utils.errorHandler per gruppo, loading tolta a tutti gli host.
class ReloadUrlGroup {
  static FIRST_FRAGMENT = 'helper-product_json';

  hosts = new Set();  // host membri, in ordine di registrazione
  requestId = 0;      // progressivo dell'ultima richiesta lanciata

  constructor(id) {
    this.id = id;
    this.unwatchQuery = ReloadUrl.watchQuery(this.reload);
    window.addEventListener(Utils.E_CART_UPDATED, this.onCart);
    window.addEventListener(Utils.E_CART_REMOVED, this.onCart);
    window.addEventListener(Utils.E_CART_ADDED, this.onCart);
  }

  add = (host) => {
    this.hosts.add(host);
  }

  // Ultimo host uscito: il coordinatore si smonta; il prossimo host del gruppo ne crea uno nuovo.
  remove = (host) => {
    this.hosts.delete(host);
    if(this.hosts.size) return;
    this.unwatchQuery();
    window.removeEventListener(Utils.E_CART_UPDATED, this.onCart);
    window.removeEventListener(Utils.E_CART_REMOVED, this.onCart);
    window.removeEventListener(Utils.E_CART_ADDED, this.onCart);
    if(ReloadUrl.groups.get(this.id) === this) {
      ReloadUrl.groups.delete(this.id);
    }
  }

  onCart = () => {
    if([...this.hosts].some((host) => host.dataset.cart != undefined)) {
      this.reload({ url: location.href });
    }
  }

  reload = (query) => {
    if(!this.hosts.size) return;
    // Log disattivato (INP): console.log costa ~0,7 ms per click sui device lenti.
    // console.log('ReloadUrl: query changed (group ' + this.id + ')', query);
    const requestId = ++this.requestId;
    const hosts = [...this.hosts];
    // loading in un requestAnimationFrame (INP): aggiunta qui, nel task del click, era la prima
    // cosa a sporcare lo stile, e un osservatore esterno (microtask di script terzi) forzava un
    // ricalcolo di ~630 elementi (~11 ms) dentro il processing dell'interazione. Nel rAF lo
    // spinner compare comunque al primo frame. Se nel frattempo la richiesta e' stata superata
    // da una piu' recente non si aggiunge nulla; se e' gia' terminata, il finally cancella il rAF.
    let loadingFrame = requestAnimationFrame(() => {
      loadingFrame = null;
      if(requestId !== this.requestId) return;
      hosts.forEach((host) => host.classList.add('loading'));
    });
    const url = new URL(query.url);
    url.searchParams.set('section_id', this.id);

    axios.get(url.toString()).then((response) => {
      if(requestId !== this.requestId) return; // superata da una richiesta piu' recente
      this.apply(response.data);
    }).catch((error) => {
      if(requestId !== this.requestId) return;
      Utils.errorHandler({
        message: error.response?.data?.message || error.message,
        target: 'reload-url',
        error: error,
        type: 'axios'
      });
    }).finally(() => {
      if(loadingFrame !== null) cancelAnimationFrame(loadingFrame);
      if(requestId !== this.requestId) return;
      new Set([...hosts, ...this.hosts]).forEach((host) => host.classList.remove('loading'));
    });
  }

  apply = (html) => {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const fragments = new Map();
    tpl.content.querySelectorAll('template[data-fragment]').forEach((fragment) => {
      if(!fragments.has(fragment.dataset.fragment)) {
        fragments.set(fragment.dataset.fragment, fragment);
      }
    });

    const first = ReloadUrlGroup.FIRST_FRAGMENT;
    const hosts = [...this.hosts].filter((host) => host.isConnected);
    const ordered = [
      ...hosts.filter((host) => host.dataset.section === first),
      ...hosts.filter((host) => host.dataset.section !== first)
    ];
    ordered.forEach((host) => {
      const fragment = fragments.get(host.dataset.section);
      if(fragment) {
        // importNode del content gia' parsato + replaceChildren: nessuna seconda parse HTML
        // (host.innerHTML = fragment.innerHTML riparserebbe la stringa per ogni host).
        host.replaceChildren(document.importNode(fragment.content, true));
      }
    });
  }
}

if(!customElements.get('reload-url')) {
  customElements.define('reload-url', ReloadUrl);
}

// Web Component: VariantChanger
// Selettore di una variante (griglia taglie e barra sticky): al click, o Enter/Space da
// tastiera, pubblica 'product:interest' e aggiorna l'URL con replaceState, che fa ricaricare
// i reload-url della pagina.
// Attributi: data-url (URL della variante); figlio script[data-describe] con { variant }.
// Lazy parse (INP): a ogni cambio variante i reload-url ricreano TUTTI i variant-changer di
// griglia e sticky (N per lato). Prima ognuno, in connectedCallback, parsava due volte l'intero
// #product-json (prodotto completo con varianti, immagini e descrizione) piu' il proprio
// [data-describe]: con 12 taglie, 48 parse del prodotto a ogni swap, sul main thread mentre la
// pagina risponde al click — peggio sui device di fascia bassa. Ora connectedCallback registra
// solo i listener, e i dati si leggono al click: prodotto e valuta dalla cache condivisa
// MainProduct.getProductJson() (un parse per nodo #product-json), la variante dal proprio
// [data-describe], parsato una volta sola e memorizzato sull'istanza.
// Payload di 'product:interest' invariato ({ ...variant, product, currencyCode }): forma
// concordata con l'agenzia di tracking, non va cambiata.
class VariantChanger extends HTMLElement {
  variantData; variantParsed = false;

  connectedCallback() {
    this.addEventListener('click', this.changeVariant.bind(this));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        this.changeVariant(e);
      }
    });
  }

  // Parse lazy del [data-describe]: al primo click, una volta per istanza (l'istanza viene
  // comunque ricreata dal reload-url al cambio variante).
  getVariantData = () => {
    if(!this.variantParsed) {
      this.variantParsed = true;
      try {
        this.variantData = JSON.parse(this.querySelector("[data-describe]")?.textContent)?.variant;
      } catch (error) {
        console.error('VariantChanger: error parsing variant JSON', error);
      }
    }
    return this.variantData;
  }

  changeVariant = (e) => {
    e.preventDefault();
    const json = MainProduct.getProductJson();
    //Added 11/02/26: publish variant change event for product interest tracking
    Shopify.analytics.publish('product:interest', { ...this.getVariantData(), product: json?.product, currencyCode: json?.currencyCode });
    if(!this.dataset.url) {
      console.error('VariantChanger: no URL specified in data-url attribute.');
      return;
    }
    window.history.replaceState({}, '', this.dataset.url);
  }
}
if(!customElements.get('variant-changer')) {
  customElements.define('variant-changer', VariantChanger);
}

class CartAdd extends HTMLElement {
  loading = false;

  connectedCallback() {
    this.addEventListener('click', this.prepare.bind(this));
  }
  
  prepare = () => {
    if(!this.checkAttributes()) return;

    if(this.dataset.formSelector != undefined) {
      setTimeout(() => {
        const form = document.querySelector(this.dataset.formSelector);
        if(!(form instanceof HTMLFormElement)){
          console.error('CartAdd: selected element is not a form.', form);
          return;
        }
        // Log disattivato (INP): console.log costa ~0,7 ms per click sui device lenti.
        // console.log('Aggiunta al carrello tramite form', form);
        this.add(form);
      });
      return;
    }

    const variantId = parseInt(this.dataset.variant);
    const quantity = parseInt(this.dataset.quantity || '1');
    const data = { 
      items: [
        { id: variantId, quantity: quantity }
      ]
    };
    // Log disattivato (INP): console.log costa ~0,7 ms per click sui device lenti.
    // console.log('Aggiunta al carrello via js', data);
    this.add(data);
  }

  add = (data) => {
    if(this.loading) return;
    // Log disattivato (INP): console.log costa ~0,7 ms per click sui device lenti.
    // console.log('Aggiunta al carrello con bottone', data);
    this.loading = true;
    this.classList.add('loading');

    const options = {};
    if(data instanceof HTMLFormElement) {
      options.form = data;
    } else {
      options.data = data;
    }

    Utils.addTocart({
      ...options,
      finally: () => {
        this.loading = false;
        this.classList.remove('loading');
      }
    });
  }

  checkAttributes = () => {
    if(this.dataset.variant == undefined && this.dataset.formSelector == undefined) {
      console.error('CartAdd: either data-variant or data-form-selector attribute must be specified.');
      return false;
    }
    return true;
  }
}
if(!customElements.get('cart-add')) {
  customElements.define('cart-add', CartAdd);
}